import React, { useState } from 'react';
import { useMutation } from '@apollo/client';
import { useNotify } from 'react-admin';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  MenuItem,
  Grid,
  Typography,
  Divider,
} from '@mui/material';
import { PROVISION_PARTNER } from './partners.queries';

// Porte ++ (ADR 0022, #1008) — formulaire admin de provisionnement d'un Partner structuré
// (graphe complet + Account natif), appelant adminProvisionPartner (garde PARTNERS_WRITE).

const SECTORS = [
  'FOOD_BEVERAGE', 'RETAIL_COMMERCE', 'BEAUTY_WELLNESS', 'FASHION', 'CRAFT_ARTISAN',
  'TECH_DIGITAL', 'HEALTH', 'EDUCATION', 'CHILDCARE_YOUTH', 'SPORT_LEISURE',
  'ENTERTAINMENT', 'TRANSPORT', 'HOSPITALITY', 'PROFESSIONAL', 'AGRICULTURE',
  'CONSTRUCTION', 'FINANCE', 'MEDIA_EVENT', 'OTHER',
];
const PLANS = ['STARTER', 'GROWTH', 'PREMIUM', 'ENTERPRISE'];

// Palette EKAA B2C + 4 paliers par défaut (l'admin ne saisit pas ces détails).
const DEFAULT_PALETTE = {
  primary: '#6B0807',
  secondary: '#E5B535',
  accent: '#8FB63C',
  background: '#FBFBE9',
  text: '#2A1A0E',
};
const DEFAULT_TIERS = [
  { name: 'Bronze', slug: 'bronze', threshold: 0, rank: 1, badgeColor: '#CD7F32' },
  { name: 'Silver', slug: 'silver', threshold: 500, rank: 2, badgeColor: '#C0C0C0' },
  { name: 'Gold', slug: 'gold', threshold: 2000, rank: 3, badgeColor: '#E5B535' },
  { name: 'VIP', slug: 'vip', threshold: 5000, rank: 4, badgeColor: '#6B0807' },
];

interface Props {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export default function ProvisionPartnerDialog({ open, onClose, onSuccess }: Props) {
  const notify = useNotify();
  const [provision, { loading }] = useMutation(PROVISION_PARTNER);

  const [form, setForm] = useState({
    name: '',
    plan: 'GROWTH',
    description: '',
    ownerEmail: '',
    ownerDisplayName: '',
    ownerSector: 'RETAIL_COMMERCE',
    ownerCountry: 'CI',
    locationName: '',
    locationCode: '',
    locationCity: '',
    locationAddress: '',
    programName: '',
  });

  const set = (k: string) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  const canSubmit =
    form.name.trim() &&
    form.ownerEmail.trim() &&
    form.locationName.trim() &&
    form.locationCode.trim() &&
    form.locationCity.trim();

  const handleSubmit = async () => {
    try {
      const input = {
        name: form.name.trim(),
        plan: form.plan,
        description: form.description || undefined,
        palette: DEFAULT_PALETTE,
        owner: {
          email: form.ownerEmail.trim(),
          displayName: form.ownerDisplayName || undefined,
          sector: form.ownerSector,
          country: form.ownerCountry.trim(),
          city: form.locationCity.trim(),
        },
        location: {
          code: form.locationCode.trim(),
          name: form.locationName.trim(),
          city: form.locationCity.trim(),
          address: form.locationAddress || undefined,
        },
        loyalty: {
          programName: form.programName.trim() || `${form.name.trim()} Fidélité`,
          tiers: DEFAULT_TIERS,
        },
      };
      const { data, errors } = await provision({ variables: { input } });
      const result = data?.adminProvisionPartner;
      // Garde anti-silent-fail : si errorPolicy='all', les erreurs GraphQL ne throwent pas —
      // elles reviennent dans `errors` avec data null. NE PAS conclure succès sans partnerId.
      if (errors?.length || !result?.partnerId) {
        notify(`Échec du provisionnement : ${errors?.[0]?.message ?? 'aucun résultat renvoyé'}`, {
          type: 'error',
        });
        return;
      }
      notify(`Partner provisionné : ${result.slug}`, { type: 'success' });
      onSuccess?.();
      onClose();
    } catch (e: any) {
      notify(`Échec du provisionnement : ${e?.message ?? 'erreur'}`, { type: 'error' });
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Provisionner un partenaire (porte ++)</DialogTitle>
      <DialogContent dividers>
        <Typography variant="subtitle2" gutterBottom>Enseigne</Typography>
        <Grid container spacing={2}>
          <Grid item xs={8}>
            <TextField label="Nom de l'enseigne" value={form.name} onChange={set('name')} fullWidth required />
          </Grid>
          <Grid item xs={4}>
            <TextField label="Plan" value={form.plan} onChange={set('plan')} select fullWidth>
              {PLANS.map((p) => <MenuItem key={p} value={p}>{p}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={12}>
            <TextField label="Description" value={form.description} onChange={set('description')} fullWidth />
          </Grid>
        </Grid>

        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle2" gutterBottom>Propriétaire</Typography>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <TextField label="Email owner" value={form.ownerEmail} onChange={set('ownerEmail')} fullWidth required />
          </Grid>
          <Grid item xs={6}>
            <TextField label="Nom affiché" value={form.ownerDisplayName} onChange={set('ownerDisplayName')} fullWidth />
          </Grid>
          <Grid item xs={8}>
            <TextField label="Secteur" value={form.ownerSector} onChange={set('ownerSector')} select fullWidth>
              {SECTORS.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
            </TextField>
          </Grid>
          <Grid item xs={4}>
            <TextField label="Pays" value={form.ownerCountry} onChange={set('ownerCountry')} fullWidth />
          </Grid>
        </Grid>

        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle2" gutterBottom>Point de vente</Typography>
        <Grid container spacing={2}>
          <Grid item xs={8}>
            <TextField label="Nom du point de vente" value={form.locationName} onChange={set('locationName')} fullWidth required />
          </Grid>
          <Grid item xs={4}>
            <TextField label="Code" value={form.locationCode} onChange={set('locationCode')} fullWidth required />
          </Grid>
          <Grid item xs={4}>
            <TextField label="Ville" value={form.locationCity} onChange={set('locationCity')} fullWidth required />
          </Grid>
          <Grid item xs={8}>
            <TextField label="Adresse" value={form.locationAddress} onChange={set('locationAddress')} fullWidth />
          </Grid>
        </Grid>

        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle2" gutterBottom>Fidélité</Typography>
        <TextField
          label="Nom du programme"
          value={form.programName}
          onChange={set('programName')}
          fullWidth
          placeholder={form.name ? `${form.name} Fidélité` : 'Programme fidélité'}
          helperText="Palette EKAA + 4 paliers (Bronze/Silver/Gold/VIP) appliqués par défaut."
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>Annuler</Button>
        <Button onClick={handleSubmit} variant="contained" disabled={!canSubmit || loading}>
          {loading ? 'Provisionnement…' : 'Provisionner'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
